import { DIFF_TYPE } from '../../../../../../constnats/diff';
import type {
    FieldDiffDescriptorType,
    ReportsDiffType,
    SchemaDiffType,
} from '../../../../../../schemas/schemaDiff.schemas';
import type { DescriptorType } from '../../../../../../types/descriptor.types';
import CloneUtils from '../../../../../../utils/cloneUtils';
import DescriptorUtils from '../../../../../../utils/descriptor/descriptorUtils';
import { REPORT_FIELD_DIFF_PROPERTY } from './constants';
import { isPropDiffChanged } from './diffPredicates';
import { getDiffDescriptorId } from './descriptorPairs';
import type { DescriptorPairLookup } from './descriptorPairs';
import type { DescriptorDiff, DescriptorWithDiffType } from './types';

export function buildDescriptorDiffs(
    schemaDiff: SchemaDiffType,
    diffDescriptors: FieldDiffDescriptorType[],
    flatTargetDescriptors: DescriptorType[]
): DescriptorDiff[] {
    const descriptorToLabelMap = Object.fromEntries(
        diffDescriptors.map((descriptor) => [descriptor.id, descriptor.label]),
    );
    return schemaDiff.diffs.map((diff) => {
        const label =
            descriptorToLabelMap[diff.descriptorId] ??
            flatTargetDescriptors.find((d) => d.id === diff.descriptorId)?.label ??
            diff.descriptorId;
        const propertyDiffs = Object.entries(diff.diffs ?? {}).flatMap(([propName, propDiff]) =>
            isPropDiffChanged(propDiff)
                ? [{ propName, referenceVal: propDiff.referenceVal, targetVal: propDiff.targetVal }]
                : [],
        );
        return { descriptorId: diff.descriptorId, label, diffType: diff.diffType, propertyDiffs };
    });
}

export function summarizeDescriptors(
    descriptors: DescriptorWithDiffType[],
    reportsDiff: ReportsDiffType,
    getDescriptorPair: DescriptorPairLookup,
    coreDataDiffOnly = false,
): DescriptorWithDiffType[] {
    const diffDescriptors = reportsDiff.diffDescriptors.filter(
        ({ id }) =>
            !coreDataDiffOnly ||
            reportsDiff.reports.some((report) =>
                isPropDiffChanged(report.fieldDiffs[id]?.[REPORT_FIELD_DIFF_PROPERTY.CORE_VALUE] ?? {}),
            ),
    );
    const descriptorIds = new Set(diffDescriptors.map((descriptor) => descriptor.id));
    const flatDescriptors = (
        DescriptorUtils.flatDescriptors as (descriptors: DescriptorWithDiffType[]) => DescriptorWithDiffType[]
    )(descriptors);

    return flatDescriptors
        .filter((descriptor) => descriptorIds.has(getDiffDescriptorId(getDescriptorPair(descriptor.id))))
        .map((descriptor) => ({ ...descriptor, subFields: [] }));
}

export function reconcileDescriptors(
    referenceDescriptors: DescriptorType[],
    targetDescriptors: DescriptorType[],
    schemaDiff: SchemaDiffType,
    getDescriptorPair: DescriptorPairLookup,
): DescriptorWithDiffType[] {
    // Copy only the descriptor tree: marking removals and inserting additions
    // must not change the reference branch or its reports.
    const reconciledDescriptors: DescriptorWithDiffType[] = CloneUtils.deepClone(referenceDescriptors);

    // Property changes are displayed separately. Here we only need additions
    // and removals to assemble the tree and color its descriptors.
    const diffTypeByDescriptorId = new Map(
        schemaDiff.diffs
            .filter((diff) => diff.diffType !== DIFF_TYPE.CHANGED)
            .map((diff) => [diff.descriptorId, diff.diffType]),
    );
    const descriptorsById = new Map<string, DescriptorWithDiffType>();

    function indexReferenceDescriptors(descriptors: DescriptorWithDiffType[]): void {
        for (const descriptor of descriptors) {
            // A reference descriptor cannot be ADDED, so this is either
            // REMOVED or undefined. Removed descriptors stay visible.
            descriptor.diffType = diffTypeByDescriptorId.get(getDiffDescriptorId(getDescriptorPair(descriptor.id)));
            // Point to the actual copied descriptor, without copying it again.
            descriptorsById.set(descriptor.id, descriptor);
            indexReferenceDescriptors(descriptor.subFields);
        }
    }

    indexReferenceDescriptors(reconciledDescriptors);

    function insertAddedDescriptors(
        targetDescriptors: DescriptorType[],
        reconciledDescriptors: DescriptorWithDiffType[],
    ): void {
        // Each sibling group has its own position for the next addition.
        // Additions before the first shared descriptor go at the beginning.
        let insertionIndex = 0;

        for (const targetDescriptor of targetDescriptors) {
            const referenceDescriptorId = getDescriptorPair(targetDescriptor.id).referenceDescriptor;
            let reconciledDescriptor = referenceDescriptorId === null
                ? undefined
                : descriptorsById.get(referenceDescriptorId);

            if (!reconciledDescriptor && diffTypeByDescriptorId.get(targetDescriptor.id) === DIFF_TYPE.ADDED) {
                // Copy this descriptor; the recursive call adds its children
                // using their own ADDED entries from the backend differences.
                reconciledDescriptor = { ...targetDescriptor, diffType: DIFF_TYPE.ADDED, subFields: [] };
                reconciledDescriptors.splice(insertionIndex, 0, reconciledDescriptor);
                descriptorsById.set(reconciledDescriptor.id, reconciledDescriptor);
            }

            // Insert children into their corresponding parent's group.
            // Preserve the previous fallback when the parent is absent:
            // process its children in the current group.
            insertAddedDescriptors(
                targetDescriptor.subFields,
                reconciledDescriptor ? reconciledDescriptor.subFields : reconciledDescriptors,
            );

            if (reconciledDescriptor) {
                const descriptorIndex = reconciledDescriptors.indexOf(reconciledDescriptor);
                // Move past this descriptor so consecutive additions retain
                // target order: [A, D] + target [A, B, C, D] -> [A, B, C, D].
                // Existing descriptors keep their reference positions; a
                // descriptor in another group doesn't move this position.
                if (descriptorIndex >= 0) {
                    insertionIndex = descriptorIndex + 1;
                }
            }
        }
    }

    insertAddedDescriptors(targetDescriptors, reconciledDescriptors);
    return reconciledDescriptors;
}
